"use client";

import { useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { getHomeData } from "@/data/getHomeData";
import { clsx } from "clsx";
import { motion, AnimatePresence } from "framer-motion";

const categoryKeys = [
	{ id: "*", msgKey: "showAll" },
	{ id: "programing_lang", msgKey: "programmingLanguage" },
	{ id: "web_development", msgKey: "webDevelopment" },
	{ id: "databases", msgKey: "databases" },
	{ id: "cloud_reporting", msgKey: "cloudReporting" },
	{ id: "other", msgKey: "other" },
] as const;

export default function SkillsSection() {
	const t = useTranslations("Skills");
	const locale = useLocale() as "en" | "ro";
	const { isotopeItems } = getHomeData(locale);
	const [filter, setFilter] = useState("*");

	const filteredItems =
		filter === "*"
			? isotopeItems
			: isotopeItems.filter((item) =>
					item.category.split(" ").includes(filter),
				);

	return (
		<section className="py-20 text-center bg-white">
			<div className="w-full section-padding">
				<h3 className="text-[2.8rem] font-normal font-[400] text-black mb-8 px-[1.5rem] pt-[1.5rem]">
					{t("title")}
				</h3>

				{/* Filter buttons: match Angular .btn-izotop-filter and .button-group */}
				<div className="flex flex-wrap justify-center gap-0 mb-12 [&>button:first-child]:rounded-l-[0.5em] [&>button:last-child]:rounded-r-[0.5em]">
					{categoryKeys.map((cat) => (
						<button
							key={cat.id}
							onClick={() => setFilter(cat.id)}
							className={clsx(
								"py-[1rem] px-[1.8rem] text-[1.6rem] border-none cursor-pointer transition-colors",
								filter === cat.id
									? "bg-[#0066cc] text-white shadow-[inset_0_-1px_hsla(0,0%,0%,0.8)]"
									: "bg-[#eee] text-[#222] hover:bg-[#8cf]",
							)}
						>
							{t(cat.msgKey)}
						</button>
					))}
				</div>

				{/* Grid: items 19.5rem × 19.5rem, margin 0.8rem, match Angular .element-item */}
				<div className="skills-grid mx-auto">
					<AnimatePresence>
						{filteredItems.map((item) => (
							<motion.div
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								exit={{ opacity: 0, scale: 0.8 }}
								key={item.name}
								className="group relative w-[19.5rem] h-[19.5rem] max-[600px]:w-[8rem] max-[600px]:h-[8rem] rounded-[2rem] p-[1rem] flex flex-col items-center justify-center overflow-hidden bg-white hover:bg-[#222] transition-colors"
							>
								{/* Default: icon only (Angular shows img; hover hides it) */}
								<a
									href={item.link}
									target="_blank"
									rel="noopener noreferrer"
									className="absolute inset-0 flex flex-col items-center justify-center p-[1rem] group-hover:opacity-0 transition-opacity z-10"
									aria-label={item.name}
								>
									<div className="relative w-full h-full min-h-0 flex items-center justify-center">
										<Image
											src={item.background_url}
											alt=""
											fill
											className="object-contain"
											sizes="195px"
											quality={70}
										/>
									</div>
								</a>
								{/* Hover: black bg + name + description (Angular .element-item:hover .name / .description) */}
								<a
									href={item.link}
									target="_blank"
									rel="noopener noreferrer"
									className="absolute inset-0 flex flex-col items-center justify-center p-[1rem] opacity-0 group-hover:opacity-100 transition-opacity z-20 text-center"
								>
									<h3 className="text-[2rem] text-white underline cursor-pointer mb-1">
										{item.name}
									</h3>
									<p className="text-[1.2rem] text-[yellow] cursor-pointer line-clamp-4">
										{item.description}
									</p>
								</a>
							</motion.div>
						))}
					</AnimatePresence>
				</div>
			</div>
		</section>
	);
}
